import {ChartData, ChartOptions} from 'chart.js';
import {FC, FormEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {Bar, Line} from 'react-chartjs-2';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {GeoMap} from '@mavismart_frontend/components/geo_map';
import {Loader} from '@mavismart_frontend/components/loader';
import {HEADER_HEIGHT, MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {parseUserAgent} from '@mavismart_frontend/lib/parse_user_agent';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';
import {
  TrackingEvent,
  TrackingEventsData,
  TrackingStats,
  fetchTrackingEvents,
  fetchTrackingStats,
  useTrackingEvents,
  useTrackingStats,
} from '@mavismart_frontend/stores/tracking_store';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENTS_PAGE_SIZE = 25;
const JOURNEY_PAGE_SIZE = 50;

type PeriodKey = '7d' | '30d' | '90d' | 'custom';

const PERIOD_OPTIONS: {key: PeriodKey; label: string; days: number}[] = [
  {key: '7d', label: '7j', days: 7},
  {key: '30d', label: '30j', days: 30},
  {key: '90d', label: '90j', days: 90},
];

function periodRange(days: number): {startTs: number; endTs: number} {
  const now = Date.now();
  return {startTs: now - days * 24 * 60 * 60 * 1000, endTs: now};
}

function formatDate(ts: number, locale: string | undefined): string {
  return new Date(ts).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// Journey helpers
// ---------------------------------------------------------------------------

const ACTION_ICONS: Record<string, string> = {
  login: '🔑',
  logout: '🚪',
  'view-door': '🚪',
  'view-doors': '📋',
  'edit-door': '✏️',
  'view-door-counters': '📊',
  'view-door-messages': '💬',
  'view-notifications': '🔔',
  'manage-notifications': '🔔',
  'view-account': '👤',
  'edit-account': '👤',
  'delete-account': '🗑️',
  'manage-collaborators': '👥',
  'manage-groups': '📁',
  'manage-media': '🖼️',
  'resolve-anomaly': '⚠️',
  'accept-terms': '📝',
  'create-support-message': '✉️',
  'view-support-message': '✉️',
  'support-door-diagnostic': '🔧',
};

function actionIcon(action: string): string {
  return ACTION_ICONS[action] ?? '📌';
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDayHeader(ts: number): string {
  const d = new Date(ts);
  const day = d.getDate();
  const months = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `📅 ${day} ${month} ${year}`;
}

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) {
    return 'il y a quelques secondes';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `il y a ${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `il y a ${hours}h`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `il y a ${days}j`;
  }
  const monthsDiff = Math.floor(days / 30);
  return `il y a ${monthsDiff} mois`;
}

function geoLabel(ev: TrackingEvent): string {
  return [ev.geo?.city, ev.geo?.country].filter(Boolean).join(', ');
}

interface DayGroup {
  day: string;
  ts: number;
  events: TrackingEvent[];
}

function groupByDay(events: TrackingEvent[]): DayGroup[] {
  const map = new Map<string, DayGroup>();
  for (const ev of events) {
    const key = dayKey(ev.ts);
    let group = map.get(key);
    if (!group) {
      group = {day: key, ts: ev.ts, events: []};
      map.set(key, group);
    }
    group.events.push(ev);
  }
  // Sort days descending (most recent first)
  const groups = [...map.values()].sort((a, b) => b.ts - a.ts);
  // Sort events within each day ascending (chronological)
  for (const g of groups) {
    g.events.sort((a, b) => a.ts - b.ts);
  }
  return groups;
}

function metadataEntries(ev: TrackingEvent): [string, string][] {
  return Object.entries(ev.metadata)
    .filter(([, v]) => v !== undefined && v !== null && v !== true)
    .map(([k, v]) => [k, String(v)]);
}
// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// --- Period Selector ---

const PeriodSelector: FC<{
  period: PeriodKey;
  customStart: string;
  customEnd: string;
  onPeriodChange: (key: PeriodKey) => void;
  onCustomStartChange: (v: string) => void;
  onCustomEndChange: (v: string) => void;
}> = ({period, customStart, customEnd, onPeriodChange, onCustomStartChange, onCustomEndChange}) => {
  return (
    <PeriodBar>
      <PeriodLabel>Période :</PeriodLabel>
      {PERIOD_OPTIONS.map(opt => (
        <PeriodButton
          key={opt.key}
          $active={period === opt.key}
          onClick={() => onPeriodChange(opt.key)}
        >
          {opt.label}
        </PeriodButton>
      ))}
      <PeriodButton $active={period === 'custom'} onClick={() => onPeriodChange('custom')}>
        Personnalisé
      </PeriodButton>
      {period === 'custom' && (
        <CustomDateWrapper>
          <DateInput
            type="date"
            value={customStart}
            onChange={e => onCustomStartChange(e.target.value)}
          />
          <span>→</span>
          <DateInput
            type="date"
            value={customEnd}
            onChange={e => onCustomEndChange(e.target.value)}
          />
        </CustomDateWrapper>
      )}
    </PeriodBar>
  );
};

// --- KPI Cards ---

const KpiCards: FC<{stats: TrackingStats | undefined}> = ({stats}) => {
  const account = useAccount();
  const locale = getUserLocale(account);

  if (!stats) {
    return <Loader />;
  }

  const cards: {label: string; value: string}[] = [
    {label: 'Total événements', value: stats.totalEvents.toLocaleString(locale)},
    {label: 'Utilisateurs uniques', value: stats.uniqueUsers.toLocaleString(locale)},
    {
      label: 'Moy. / jour',
      value: stats.avgEventsPerDay.toLocaleString(locale, {maximumFractionDigits: 0}),
    },
  ];

  return (
    <KpiRow>
      {cards.map(c => (
        <KpiCard key={c.label}>
          <KpiValue>{c.value}</KpiValue>
          <KpiLabel>{c.label}</KpiLabel>
        </KpiCard>
      ))}
    </KpiRow>
  );
};

// --- Activity Line Chart ---

const ActivityLineChart: FC<{stats: TrackingStats | undefined}> = ({stats}) => {
  if (!stats || stats.eventsByDay.length === 0) {
    return null;
  }

  const labels = stats.eventsByDay.map(d => d.day);
  const values = stats.eventsByDay.map(d => d.count);

  const data: ChartData<'line', number[], string> = {
    labels,
    datasets: [
      {
        label: 'Événements',
        data: values,
        borderColor: MAIN_COLOR,
        backgroundColor: `${MAIN_COLOR}22`,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {display: false},
      legend: {display: false},
      tooltip: {enabled: true},
      title: {display: true, text: 'Activité quotidienne'},
    },
    scales: {
      y: {beginAtZero: true},
    },
  };

  return (
    <ChartContainer>
      <Line data={data} options={options} />
    </ChartContainer>
  );
};

// --- Action Breakdown Chart ---

const ActionBreakdownChart: FC<{stats: TrackingStats | undefined}> = ({stats}) => {
  if (!stats || Object.keys(stats.eventsByAction).length === 0) {
    return null;
  }

  const sorted = Object.entries(stats.eventsByAction).sort(([, a], [, b]) => b - a);
  const labels = sorted.map(([action]) => action);
  const values = sorted.map(([, count]) => count);

  const colors = [
    '#D51633',
    '#2F3A4F',
    '#FF9500',
    '#34C759',
    '#5856D6',
    '#FF2D55',
    '#AF52DE',
    '#007AFF',
    '#FF3B30',
    '#5AC8FA',
  ];

  const data: ChartData<'bar', number[], string> = {
    labels,
    datasets: [
      {
        label: 'Événements',
        data: values,
        backgroundColor: labels.map((_, i) => colors[i % colors.length] ?? '#888'),
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      datalabels: {display: false},
      legend: {display: false},
      tooltip: {enabled: true},
      title: {display: true, text: 'Répartition par action'},
    },
    scales: {
      x: {beginAtZero: true},
    },
  };

  return (
    <ChartContainer $tall>
      <Bar data={data} options={options} />
    </ChartContainer>
  );
};

// --- Top Locations ---

const TopLocations: FC<{stats: TrackingStats | undefined}> = ({stats}) => {
  if (!stats) {
    return null;
  }

  return (
    <LocationsRow>
      <MiniTable>
        <MiniTableTitle>🌍 Top régions</MiniTableTitle>
        <table>
          <tbody>
            {stats.topRegions.slice(0, 5).map(c => (
              <tr key={c.name}>
                <td>{c.name || '—'}</td>
                <MiniTableCount>{c.count}</MiniTableCount>
              </tr>
            ))}
            {stats.topRegions.length === 0 && (
              <tr>
                <td colSpan={2}>Aucune donnée</td>
              </tr>
            )}
          </tbody>
        </table>
      </MiniTable>
      <MiniTable>
        <MiniTableTitle>🏙️ Top villes</MiniTableTitle>
        <table>
          <tbody>
            {stats.topCities.slice(0, 5).map(c => (
              <tr key={c.name}>
                <td>{c.name || '—'}</td>
                <MiniTableCount>{c.count}</MiniTableCount>
              </tr>
            ))}
            {stats.topCities.length === 0 && (
              <tr>
                <td colSpan={2}>Aucune donnée</td>
              </tr>
            )}
          </tbody>
        </table>
      </MiniTable>
    </LocationsRow>
  );
};
// --- Filters ---

const EventFilters: FC<{
  emailFilter: string;
  actionFilter: string;
  actions: string[];
  onEmailChange: (v: string) => void;
  onActionChange: (v: string) => void;
}> = ({emailFilter, actionFilter, actions, onEmailChange, onActionChange}) => {
  return (
    <FiltersRow>
      <FilterInput
        type="text"
        placeholder="Filtrer par email..."
        value={emailFilter}
        onChange={e => onEmailChange(e.target.value)}
      />
      <FilterSelect value={actionFilter} onChange={e => onActionChange(e.target.value)}>
        <option value="">Toutes les actions</option>
        {actions.map(a => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </FilterSelect>
    </FiltersRow>
  );
};

// --- Events Table ---

const EventsTable: FC<{
  events: TrackingEvent[] | undefined;
  locale: string | undefined;
  onEmailClick?: (email: string) => void;
}> = ({events, locale, onEmailClick}) => {
  const [page, setPage] = useState(0);

  // Reset to first page when events change
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset page on events change
  useEffect(() => setPage(0), [events]);

  if (events === undefined) {
    return <Loader />;
  }

  if (events.length === 0) {
    return <EmptyMessage>Aucun événement trouvé</EmptyMessage>;
  }

  const start = page * EVENTS_PAGE_SIZE;
  const pageEvents = events.slice(start, start + EVENTS_PAGE_SIZE);
  const totalPages = Math.ceil(events.length / EVENTS_PAGE_SIZE);

  return (
    <div>
      <EventsTableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Email</th>
              <th>Action</th>
              <th>Route</th>
              <th>Date</th>
              <th>Localisation</th>
              <th>User Agent</th>
            </tr>
          </thead>
          <tbody>
            {pageEvents.map(ev => (
              <tr key={`${ev.email}-${ev.ts}`}>
                <td>
                  <EmailLink
                    onClick={e => {
                      e.stopPropagation();
                      onEmailClick?.(ev.email);
                    }}
                  >
                    {ev.email}
                  </EmailLink>
                </td>
                <td>
                  <ActionBadge>{ev.action}</ActionBadge>
                </td>
                <td>{ev.route}</td>
                <td>{formatDate(ev.ts, locale)}</td>
                <td>{[ev.geo?.city, ev.geo?.postalCode].filter(Boolean).join(', ') || '—'}</td>
                <UaCell title={ev.userAgent}>{parseUserAgent(ev.userAgent)}</UaCell>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </EventsTableWrapper>
      {totalPages > 1 && (
        <SimplePagination>
          <span>
            {events.length} événement{events.length > 1 ? 's' : ''}
          </span>
          <PaginationButtons>
            <PaginationBtn onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              ‹
            </PaginationBtn>
            <span>
              {page + 1} / {totalPages}
            </span>
            <PaginationBtn
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              ›
            </PaginationBtn>
          </PaginationButtons>
        </SimplePagination>
      )}
    </div>
  );
};

// --- User Journey ---

const UserSearchBar: FC<{
  email: string;
  onEmailChange: (v: string) => void;
  onSearch: () => void;
}> = ({email, onEmailChange, onSearch}) => {
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      onSearch();
    },
    [onSearch]
  );

  return (
    <SearchBarForm onSubmit={handleSubmit}>
      <SearchInput
        type="email"
        placeholder="Email utilisateur..."
        value={email}
        onChange={e => onEmailChange(e.target.value)}
      />
      <SearchButton type="submit">Rechercher</SearchButton>
    </SearchBarForm>
  );
};

const UserKpiCards: FC<{events: TrackingEvent[]}> = ({events}) => {
  const count = events.length;
  // Find the most recent event by timestamp
  const lastEvent = useMemo(() => {
    if (events.length === 0) {
      return undefined;
    }
    let latest = events[0];
    for (const ev of events) {
      if (latest === undefined || ev.ts > latest.ts) {
        latest = ev;
      }
    }
    return latest;
  }, [events]);
  const lastActivity = lastEvent ? relativeTime(lastEvent.ts) : '—';
  const lastLocation = lastEvent ? geoLabel(lastEvent) || '—' : '—';

  const cards: {label: string; value: string}[] = [
    {label: 'Actions sur la période', value: String(count)},
    {label: 'Dernière activité', value: lastActivity},
    {label: 'Dernière localisation', value: lastLocation},
  ];

  return (
    <UserKpiRow>
      {cards.map(c => (
        <KpiCard key={c.label}>
          <KpiValue>{c.value}</KpiValue>
          <KpiLabel>{c.label}</KpiLabel>
        </KpiCard>
      ))}
    </UserKpiRow>
  );
};

const UserTimeline: FC<{events: TrackingEvent[]}> = ({events}) => {
  const groups = useMemo(() => groupByDay(events), [events]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [newestFirst, setNewestFirst] = useState(true);

  const sortedGroups = useMemo(() => {
    const sorted = [...groups].sort((a, b) => (newestFirst ? b.ts - a.ts : a.ts - b.ts));
    return sorted.map(g => ({
      ...g,
      events: [...g.events].sort((a, b) => (newestFirst ? b.ts - a.ts : a.ts - b.ts)),
    }));
  }, [groups, newestFirst]);

  // Detect IP changes and logins to auto-expand (keyed by timestamp only)
  const autoExpandInfo = useMemo(() => {
    const info = new Map<string, string>();
    // Sort all events chronologically to detect IP changes correctly
    const allEvents = events.slice().sort((a, b) => a.ts - b.ts);
    let prevIp: string | undefined;
    for (const ev of allEvents) {
      const key = String(ev.ts);
      const isLogin = ev.action === 'login';
      const isIpChange = prevIp !== undefined && ev.ip !== prevIp;
      if (isLogin && isIpChange) {
        info.set(key, '🔑 Login + nouvelle IP');
      } else if (isLogin) {
        info.set(key, '🔑 Login');
      } else if (isIpChange) {
        info.set(key, "🔀 Changement d'IP");
      }
      prevIp = ev.ip;
    }
    return info;
  }, [events]);

  const toggleKey = useCallback((key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (events.length === 0) {
    return <EmptyMessage>Aucun événement trouvé pour cet utilisateur</EmptyMessage>;
  }

  return (
    <TimelineWrapper>
      <SortToggle onClick={() => setNewestFirst(v => !v)}>
        {newestFirst ? '⬇ Plus récent en premier' : '⬆ Plus ancien en premier'}
      </SortToggle>
      {sortedGroups.map(group => (
        <TimelineDayGroup key={group.day}>
          <TimelineDayHeader>{formatDayHeader(group.events[0]?.ts ?? 0)}</TimelineDayHeader>
          <TimelineEvents>
            {group.events.map(ev => {
              const meta = metadataEntries(ev);
              const key = String(ev.ts);
              const isExpanded = expandedKeys.has(key) || autoExpandInfo.has(key);
              const autoReason = autoExpandInfo.get(key);

              return (
                <TimelineEvent key={key} onClick={() => toggleKey(key)}>
                  <TimelineDot />
                  <TimelineContent>
                    <TimelineTime>{formatTime(ev.ts)}</TimelineTime>
                    <TimelineIcon>{actionIcon(ev.action)}</TimelineIcon>
                    <TimelineAction>{ev.action}</TimelineAction>
                    <TimelineRoute>({ev.route})</TimelineRoute>
                    {meta.length > 0 && (
                      <TimelineMeta>
                        {meta.map(([k, v]) => (
                          <span key={k}>
                            {k}: {`"${v}"`}
                          </span>
                        ))}
                      </TimelineMeta>
                    )}
                    {isExpanded && (
                      <TimelineGeo>
                        {autoReason && <AutoExpandReason>{autoReason}</AutoExpandReason>}
                        {[
                          ev.ip || undefined,
                          ev.geo?.city ? decodeURIComponent(ev.geo.city) : undefined,
                          ev.geo?.postalCode,
                          ev.geo?.region ? decodeURIComponent(ev.geo.region) : undefined,
                          ev.geo?.country ? decodeURIComponent(ev.geo.country) : undefined,
                          parseUserAgent(ev.userAgent),
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </TimelineGeo>
                    )}
                  </TimelineContent>
                </TimelineEvent>
              );
            })}
          </TimelineEvents>
        </TimelineDayGroup>
      ))}
    </TimelineWrapper>
  );
};

const UserJourneyTab: FC<{
  period: PeriodKey;
  customStart: string;
  customEnd: string;
  onPeriodChange: (key: PeriodKey) => void;
  onCustomStartChange: (v: string) => void;
  onCustomEndChange: (v: string) => void;
  startTs: number;
  endTs: number;
  initialEmail: string;
  onEmailSearch: (email: string) => void;
}> = ({
  period,
  customStart,
  customEnd,
  onPeriodChange,
  onCustomStartChange,
  onCustomEndChange,
  startTs,
  endTs,
  initialEmail,
  onEmailSearch,
}) => {
  const [journeyEmail, setJourneyEmail] = useState(initialEmail);
  const [journeyEvents, setJourneyEvents] = useState<TrackingEvent[]>([]);
  const [journeyToken, setJourneyToken] = useState<string | undefined>(undefined);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const doSearch = useCallback(
    async (email: string, append = false) => {
      if (!email.trim()) {
        return;
      }
      setJourneyLoading(true);
      try {
        const response = await fetchTrackingEvents({
          email: email.trim(),
          startTs,
          endTs,
          limit: JOURNEY_PAGE_SIZE,
          paginationToken: append ? journeyToken : undefined,
        });
        if (response) {
          const newEvents = (response as TrackingEventsData).events;
          const token = (response as TrackingEventsData).nextPaginationToken;
          setJourneyEvents(prev => (append ? [...prev, ...newEvents] : newEvents));
          setJourneyToken(token);
        }
      } finally {
        setJourneyLoading(false);
        setHasSearched(true);
      }
    },
    [startTs, endTs, journeyToken]
  );

  // Auto-search when initialEmail is provided (from URL)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger on initialEmail change
  useEffect(() => {
    if (initialEmail) {
      setJourneyEmail(initialEmail);
      void doSearch(initialEmail, false);
    }
  }, [initialEmail]);

  const handleSearch = useCallback(() => {
    setJourneyEvents([]);
    setJourneyToken(undefined);
    onEmailSearch(journeyEmail.trim());
    void doSearch(journeyEmail.trim(), false);
  }, [doSearch, journeyEmail, onEmailSearch]);

  const handleLoadMore = useCallback(() => {
    void doSearch(journeyEmail.trim(), true);
  }, [doSearch, journeyEmail]);

  return (
    <>
      <UserSearchBar email={journeyEmail} onEmailChange={setJourneyEmail} onSearch={handleSearch} />
      <PeriodSelector
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onPeriodChange={onPeriodChange}
        onCustomStartChange={onCustomStartChange}
        onCustomEndChange={onCustomEndChange}
      />
      {journeyLoading && !hasSearched ? (
        <Loader />
      ) : hasSearched ? (
        <>
          <UserKpiCards events={journeyEvents} />
          <UserTimeline events={journeyEvents} />
          {journeyToken && (
            <LoadMoreWrapper>
              <LoadMoreButton onClick={handleLoadMore} disabled={journeyLoading}>
                {journeyLoading ? 'Chargement...' : 'Charger plus ▾'}
              </LoadMoreButton>
            </LoadMoreWrapper>
          )}
        </>
      ) : null}
    </>
  );
};
// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const DesktopTracking: FC = () => {
  const account = useAccount();
  const locale = getUserLocale(account);
  const lang = getUserLanguage(account);
  const [location, setLocation] = useLocation();

  // Tab state synced with URL: /tracking, /tracking/journey, /tracking/journey/{email}
  const activeTab = location.startsWith('/tracking/journey') ? 'journey' : 'overview';
  const urlEmail =
    activeTab === 'journey'
      ? decodeURIComponent(
          location.replace('/tracking/journey/', '').replace('/tracking/journey', '')
        )
      : '';
  const setActiveTab = useCallback(
    (tab: 'overview' | 'journey') => {
      setLocation(tab === 'journey' ? '/tracking/journey' : '/tracking');
    },
    [setLocation]
  );

  // Period state
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Filter state
  const [emailFilter, setEmailFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  // Data from stores
  const eventsData = useTrackingEvents();
  const stats = useTrackingStats();

  // Compute time range
  const {startTs, endTs} = useMemo(() => {
    if (period === 'custom' && customStart && customEnd) {
      return {
        startTs: new Date(customStart).getTime(),
        endTs: new Date(customEnd).getTime() + 24 * 60 * 60 * 1000 - 1,
      };
    }
    const defaultOpt = {key: '30d' as PeriodKey, label: '30j', days: 30};
    const opt = PERIOD_OPTIONS.find(o => o.key === period) ?? defaultOpt;
    return periodRange(opt.days);
  }, [period, customStart, customEnd]);

  // Fetch stats when period changes
  useEffect(() => {
    fetchTrackingStats(startTs, endTs);
  }, [startTs, endTs]);

  // Fetch events when period or action filter change
  useEffect(() => {
    fetchTrackingEvents({
      startTs,
      endTs,
      action: actionFilter || undefined,
      limit: 1000,
    });
  }, [startTs, endTs, actionFilter]);

  // Filter events client-side for "contains" email search
  const filteredEvents = useMemo(() => {
    if (!eventsData?.events) {
      return undefined;
    }
    if (!emailFilter) {
      return eventsData.events;
    }
    const lower = emailFilter.toLowerCase();
    return eventsData.events.filter(ev => ev.email.toLowerCase().includes(lower));
  }, [eventsData, emailFilter]);

  // Derive unique action list from stats for the filter dropdown
  const actionList = useMemo(() => {
    if (!stats?.eventsByAction) {
      return [];
    }
    return Object.keys(stats.eventsByAction).sort();
  }, [stats]);

  // Suppress unused variable warning — lang is available for future translations
  void lang;

  return (
    <PageWrapper>
      {/* Tabs */}
      <TabsBar>
        <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Vue d'ensemble
        </Tab>
        <Tab $active={activeTab === 'journey'} onClick={() => setActiveTab('journey')}>
          Parcours utilisateur
        </Tab>
      </TabsBar>

      {activeTab === 'overview' ? (
        <ContentScroll>
          <PeriodSelector
            period={period}
            customStart={customStart}
            customEnd={customEnd}
            onPeriodChange={setPeriod}
            onCustomStartChange={setCustomStart}
            onCustomEndChange={setCustomEnd}
          />

          <KpiCards stats={stats} />

          <ChartsRow>
            <ActivityLineChart stats={stats} />
            <ActionBreakdownChart stats={stats} />
          </ChartsRow>

          <GeoMap events={eventsData?.events} locale={locale} />

          <TopLocations stats={stats} />

          <SectionTitle>Événements récents</SectionTitle>

          <EventFilters
            emailFilter={emailFilter}
            actionFilter={actionFilter}
            actions={actionList}
            onEmailChange={setEmailFilter}
            onActionChange={setActionFilter}
          />

          <EventsTable
            events={filteredEvents}
            locale={locale}
            onEmailClick={email => setLocation(`/tracking/journey/${encodeURIComponent(email)}`)}
          />
        </ContentScroll>
      ) : (
        <ContentScroll>
          <UserJourneyTab
            period={period}
            customStart={customStart}
            customEnd={customEnd}
            onPeriodChange={setPeriod}
            onCustomStartChange={setCustomStart}
            onCustomEndChange={setCustomEnd}
            startTs={startTs}
            endTs={endTs}
            initialEmail={urlEmail}
            onEmailSearch={email => setLocation(`/tracking/journey/${encodeURIComponent(email)}`)}
          />
        </ContentScroll>
      )}
    </PageWrapper>
  );
};

DesktopTracking.displayName = 'DesktopTracking';
// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - ${HEADER_HEIGHT}px - env(safe-area-inset-top));
  margin-top: calc(${HEADER_HEIGHT}px + env(safe-area-inset-top));
`;

const TabsBar = styled.div`
  display: flex;
  border-bottom: 1px solid #eef1f4;
  background: white;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Tab = styled.div<{$active: boolean}>`
  flex: 1;
  text-align: center;
  padding: 14px 0;
  cursor: pointer;
  font-size: 16px;
  color: ${p => (p.$active ? MAIN_COLOR : TEXT_MAIN_COLOR)};
  border-bottom: 2px solid ${p => (p.$active ? MAIN_COLOR : 'transparent')};
  transition: border-color 0.2s, color 0.2s;
  &:hover {
    color: ${MAIN_COLOR};
  }
`;

const ContentScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Period

const PeriodBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PeriodLabel = styled.span`
  font-weight: 600;
  color: ${TEXT_MAIN_COLOR};
`;

const PeriodButton = styled.button<{$active: boolean}>`
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid ${p => (p.$active ? MAIN_COLOR : '#ccc')};
  background: ${p => (p.$active ? MAIN_COLOR : 'white')};
  color: ${p => (p.$active ? 'white' : TEXT_MAIN_COLOR)};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
  &:hover {
    border-color: ${MAIN_COLOR};
  }
`;

const CustomDateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateInput = styled.input`
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
`;

// KPI

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const KpiCard = styled.div`
  background: #f8f9fb;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const KpiValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${TEXT_MAIN_COLOR};
`;

const KpiLabel = styled.div`
  font-size: 13px;
  color: #8a8e95;
`;

// Charts

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ChartContainer = styled.div<{$tall?: boolean}>`
  background: #f8f9fb;
  border-radius: 10px;
  padding: 16px;
  height: ${p => (p.$tall ? '350px' : '280px')};
`;

// Locations

const LocationsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const MiniTable = styled.div`
  background: #f8f9fb;
  border-radius: 10px;
  padding: 16px;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 6px 4px;
    font-size: 14px;
    color: ${TEXT_MAIN_COLOR};
    border-bottom: 1px solid #eef1f4;
  }
`;

const MiniTableTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
  color: ${TEXT_MAIN_COLOR};
`;

const MiniTableCount = styled.td`
  text-align: right;
  font-weight: 600;
`;

// Filters

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  min-width: 220px;
  &:focus {
    outline: none;
    border-color: ${TEXT_MAIN_COLOR};
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 180px;
  &:focus {
    outline: none;
    border-color: ${TEXT_MAIN_COLOR};
  }
`;

// Events Table

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${TEXT_MAIN_COLOR};
  margin: 0;
`;

const EventsTableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 10px 8px;
    background: #f8f9fb;
    color: #8a8e95;
    font-weight: 600;
    white-space: nowrap;
    border-bottom: 2px solid #eef1f4;
  }

  td {
    padding: 8px;
    border-bottom: 1px solid #eef1f4;
    color: ${TEXT_MAIN_COLOR};
    vertical-align: top;
  }

  tbody tr:hover {
    background: #f8f9fb;
  }
`;

const ActionBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #eef1f4;
  font-size: 12px;
  font-weight: 500;
`;

const EmailLink = styled.span`
  color: ${MAIN_COLOR};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const UaCell = styled.td`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #8a8e95;
  font-size: 14px;
`;

const SimplePagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 13px;
  color: #8a8e95;
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  &:hover:not(:disabled) {
    border-color: ${MAIN_COLOR};
  }
`;

// User Journey

const SearchBarForm = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  min-width: 260px;
  &:focus {
    outline: none;
    border-color: ${TEXT_MAIN_COLOR};
  }
`;

const SearchButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: ${MAIN_COLOR};
  color: white;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover {
    background: #ba1d35;
  }
`;

const UserKpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SortToggle = styled.button`
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background: white;
  color: ${TEXT_MAIN_COLOR};
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
  &:hover {
    border-color: ${MAIN_COLOR};
    color: ${MAIN_COLOR};
  }
`;

const TimelineDayGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineDayHeader = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${TEXT_MAIN_COLOR};
  padding: 8px 0;
`;

const TimelineEvents = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 12px;
  border-left: 2px solid #dde1e6;
  gap: 0;
`;

const TimelineEvent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  position: relative;
  cursor: pointer;
  &:hover {
    background: #f0f2f5;
    border-radius: 6px;
  }
`;

const TimelineDot = styled.div`
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 50%;
  background: ${MAIN_COLOR};
  margin-top: 5px;
  margin-left: -18px;
`;

const TimelineContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
  color: ${TEXT_MAIN_COLOR};
`;

const TimelineTime = styled.span`
  font-weight: 600;
  color: #8a8e95;
  min-width: 42px;
`;

const TimelineIcon = styled.span`
  font-size: 16px;
`;

const TimelineAction = styled.span`
  font-weight: 700;
`;

const TimelineRoute = styled.span`
  color: #8a8e95;
  font-size: 13px;
`;

const TimelineMeta = styled.div`
  width: 100%;
  font-size: 12px;
  color: #8a8e95;
  padding-left: 48px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TimelineGeo = styled.div`
  width: 100%;
  font-size: 12px;
  color: #6b7280;
  padding-left: 48px;
  font-style: italic;
`;

const AutoExpandReason = styled.span`
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  margin-right: 6px;
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;

const LoadMoreButton = styled.button`
  padding: 10px 28px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background: white;
  color: ${TEXT_MAIN_COLOR};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: ${MAIN_COLOR};
    color: ${MAIN_COLOR};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
